import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, formatCategoryDisplay } from "@/lib/utils";
import {
  DOCUMENT_TAG_PILL_CLASS,
  DocumentTagPill,
  documentTagToneClass,
  type DocumentTagTone,
} from "../atoms/DocumentTagPill";
import {
  badgeMeasureCount,
  badgeMeasureCounts,
  fitTagsOnOneLine,
  TAG_GAP,
} from "@/domains/documents/utils/fitTagsOnOneLine";
import { onFontsLoaded } from "@/domains/documents/utils/onFontsLoaded";

export interface TagReference {
  type: "project" | "folder" | "category" | "construction" | "stage";
  id: string;
  name: string;
}

interface DocumentTagsProps {
  /** Array of tag objects */
  tags: TagReference[];
  /** Maximum visible tags before "+N more" — used as fallback before dynamic measurement (default: 3) */
  maxVisible?: number;
  /** Optional click handler for tags */
  onTagClick?: (tag: TagReference) => void;
  /** Optional handler to remove a tag (shows X button on hover) */
  onTagRemove?: (tag: TagReference) => void;
  /** Show all tags (ignores maxVisible and dynamic layout) */
  showAll?: boolean;
  /** Callback when show all/collapse is toggled */
  onToggleShowAll?: () => void;
  /** Callback when the "+N more" overflow badge is clicked (e.g. to open tag editor) */
  onOverflowClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Optional element rendered inline after the last tag */
  suffix?: ReactNode;
  /**
   * Keep to one line instead of two, so the row around it is the same height
   * with tags or without (the Brief page hides them while Franklin's
   * suggestions are open, and its rows must not move when they do).
   */
  singleLine?: boolean;
}

const TAG_TONES: Record<TagReference["type"], DocumentTagTone> = {
  project: "category",
  folder: "category",
  category: "category",
  construction: "discipline",
  stage: "stage",
};

/**
 * On one line the "+N more" badge never shrinks or wraps: a squeezed badge
 * breaks onto two lines and makes the row taller. The two-line layout keeps
 * its old behaviour, since it only estimates the badge's room.
 */
const OVERFLOW_BADGE_CLASS = "flex-shrink-0 whitespace-nowrap";
/** One line's floor for a squeezed pill: a letter and an ellipsis. Drawn and
 *  measured pills both carry it, or the fit undercounts short tags. */
const PILL_FLOOR_CLASS = "min-w-12";

/** Tag types that can be removed via X button (folders are structural, not removable) */
const REMOVABLE_TAG_TYPES: Set<TagReference["type"]> = new Set([
  "category",
  "construction",
  "stage",
]);

/**
 * DocumentTags - Molecule component
 *
 * Display document tags as the Issues page's outline pills.
 * Uses dynamic 2-row responsive layout — shows as many tags as fit
 * in 2 rows of the container width, with "+N more" for overflow.
 * Optionally shows an X inside the pill on hover for removable tags. With no
 * tags it still renders `suffix`, so the tag editor stays reachable.
 */
export function DocumentTags({
  tags,
  maxVisible = 3,
  onTagClick,
  onTagRemove,
  showAll = false,
  onToggleShowAll,
  onOverflowClick,
  className,
  suffix,
  singleLine: singleLineRequested = false,
}: DocumentTagsProps) {
  // "Show all" is a request to see every tag, which one clipped line cannot
  // honour: it wraps as before.
  const singleLine = singleLineRequested && !showAll;
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const suffixRef = useRef<HTMLSpanElement | null>(null);
  const hasSuffix = Boolean(suffix);
  // The observer outlives the suffix coming and going: the suffix element
  // joins and leaves it through its ref, never by reinstalling the observer.
  const observerRef = useRef<ResizeObserver | null>(null);
  const singleLineRef = useRef(singleLine);
  singleLineRef.current = singleLine;
  const setSuffixEl = useCallback((el: HTMLSpanElement | null) => {
    if (suffixRef.current) observerRef.current?.unobserve(suffixRef.current);
    suffixRef.current = el;
    if (el && singleLineRef.current) observerRef.current?.observe(el);
  }, []);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const calculateVisibleCount = useCallback(() => {
    const measureEl = measureRef.current;
    const containerEl = containerRef.current;
    if (!measureEl || !containerEl || tags.length === 0) return;

    const containerWidth = containerEl.offsetWidth;
    if (containerWidth === 0) {
      // Nothing to fit against (off-screen, collapsed). On one line, drop to
      // unmeasured (one tag and the badge) rather than keep a count fitted to
      // other tags.
      if (singleLine) setVisibleCount(null);
      return;
    }

    // Measure each tag child in the hidden measurement container (the badges
    // measured beside them are not tags)
    const measured = Array.from(measureEl.children) as HTMLElement[];
    const children = measured.filter((child) => child.dataset.badgeMeasure === undefined);
    const badgeWidths = new Map(
      measured
        .filter((child) => child.dataset.badgeMeasure !== undefined)
        .map((child) => [Number(child.dataset.badgeMeasure), child.offsetWidth])
    );

    if (singleLine) {
      const suffixWidth = suffixRef.current?.offsetWidth ?? 0;
      const room = Math.max(0, containerWidth - (suffixWidth > 0 ? suffixWidth + TAG_GAP : 0));
      const fit = fitTagsOnOneLine(
        children.map((child) => child.offsetWidth),
        room,
        // 0 = not laid out yet: keep the estimate rather than no room at all.
        (hidden) => badgeWidths.get(badgeMeasureCount(hidden, tags.length - 1))
      );
      setVisibleCount((prev) => (prev === fit ? prev : fit));
      return;
    }

    let currentRowWidth = 0;
    let rowCount = 1;
    let fitCount = 0;

    // Reserve space for "+N more" badge (~60px)
    const overflowBadgeWidth = 60;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;

      if (currentRowWidth + childWidth > containerWidth) {
        // This tag doesn't fit on the current row
        rowCount++;
        if (rowCount > 2) {
          // We've exceeded 2 rows — check if we need space for the overflow badge
          // Go back and check if the badge fits in remaining space of row 2
          break;
        }
        currentRowWidth = childWidth + TAG_GAP;
      } else {
        currentRowWidth += childWidth + TAG_GAP;
      }

      // Check if remaining tags after this one would need an overflow badge
      const remainingTags = tags.length - (i + 1);
      if (rowCount <= 2) {
        if (remainingTags > 0) {
          // Check if the next tag + badge would push us beyond 2 rows
          const nextChildWidth = i + 1 < children.length ? children[i + 1].offsetWidth : 0;
          const wouldOverflow = currentRowWidth + nextChildWidth > containerWidth && rowCount >= 2;

          if (wouldOverflow) {
            // Check if badge fits in remaining space
            if (currentRowWidth + overflowBadgeWidth <= containerWidth) {
              fitCount = i + 1;
            } else {
              // Badge doesn't fit, back up one tag
              fitCount = Math.max(1, i);
            }
            break;
          }
        }
        fitCount = i + 1;
      }
    }

    // If all tags fit in 2 rows, show them all. Functional update + explicit
    // equality guard ensures we never re-render for an unchanged value, even
    // if React's bail-out is short-circuited by a closure-captured stale
    // state (defends against the ResizeObserver loop documented below).
    const next = fitCount >= tags.length ? tags.length : Math.max(1, fitCount);
    setVisibleCount((prev) => (prev === next ? prev : next));
  }, [tags, singleLine]);

  // Mirror the latest `calculateVisibleCount` on a ref so the effect below
  // doesn't have to depend on the callback identity. Without this indirection
  // every parent re-render that gives us a new `tags` array reference would
  // recreate `calculateVisibleCount` (its useCallback deps are `[tags]`),
  // which would in turn re-run the effect, disconnect the previous
  // ResizeObserver, install a fresh one, and — per the ResizeObserver spec —
  // synchronously schedule one callback for the current container size. With
  // many wide tags that sit right on the wrap boundary (5 wide chips on the
  // file-row / virtualizer-managed table) the count alternated between two
  // values, the row's height oscillated, the table reflowed, the observer
  // fired again, and the row visibly bobbed up and down forever until any
  // click (or selection mutation) re-rendered with a stable height.
  const calculateRef = useRef(calculateVisibleCount);
  calculateRef.current = calculateVisibleCount;

  useEffect(() => {
    // Skip measurement if showing all or no tags
    if (showAll || tags.length === 0) return;

    // Measure in the next frame, at most one pending at a time, and never
    // after unmount.
    let frame = 0;
    const scheduleFit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => calculateRef.current());
    };

    // Initial calculation after render
    scheduleFit();

    // A web font swapping in changes every tag's width but not the cell's
    // (one line fills its column), so the observer below never hears of it:
    // refit when fonts finish loading too, through one shared subscription.
    // One line only: the two-line layout is the pre-existing one, left as it
    // was. It can hold a fit made with the fallback font until its next
    // resize, which it tolerates: it wraps rather than clipping, so a stale
    // count costs a tag more or less on the second line, never a cut one.
    const stopFonts = singleLine ? onFontsLoaded(scheduleFit) : () => undefined;

    // Observe container resize
    const containerEl = containerRef.current;
    if (!containerEl) {
      return () => {
        cancelAnimationFrame(frame);
        stopFonts();
      };
    }

    const observer = new ResizeObserver(scheduleFit);
    observerRef.current = observer;
    observer.observe(containerEl);
    // On one line the suffix's width comes out of the tags' room.
    if (singleLine && suffixRef.current) observer.observe(suffixRef.current);

    return () => {
      cancelAnimationFrame(frame);
      stopFonts();
      observer.disconnect();
      observerRef.current = null;
    };
    // Intentional: depend on `tags.length` (not the array identity) and not
    // on `calculateVisibleCount`. The observer reads the latest callback via
    // the ref above. This stops the install/disconnect/re-fire loop.
  }, [showAll, tags.length, singleLine]);

  // A count fitted to two lines is wrong on one (and the reverse): drop it and
  // refit before the browser paints, rather than painting a clipped line for
  // the frame until the effect above re-measures. One line also refits before
  // paint on mount and whenever the tags themselves change (loading in after
  // mount, a rename, one swapped for another), so a row never pops to its
  // fitted count and a stale count never clips a tag behind a wrong "+N more".
  // The suffix's width comes out of the tags' room, so its arrival or
  // departure refits too.
  const tagsKey =
    tags.map((tag) => `${tag.type}:${tag.id}:${tag.name}`).join("|") + `|suffix:${hasSuffix}`;
  const fitted = useRef<{ singleLine: boolean; tagsKey: string } | null>(null);
  useLayoutEffect(() => {
    // Expanded, keep the collapsed fit as it was: it is what "Show less"
    // returns to, and what says whether anything was hidden.
    if (showAll) return;
    const prev = fitted.current;
    fitted.current = { singleLine, tagsKey };
    const modeChanged = prev !== null && prev.singleLine !== singleLine;
    if (modeChanged) setVisibleCount(null);
    if (tags.length === 0) return;
    if (modeChanged || (singleLine && prev?.tagsKey !== tagsKey)) calculateRef.current();
  }, [singleLine, showAll, tags.length, tagsKey]);

  if (tags.length === 0 && !suffix) {
    return null;
  }

  // Use dynamic count if available, otherwise fall back to maxVisible
  // Unmeasured (first paint, a zero-width container), one line shows a single
  // tag and the "+N more" badge rather than clipping `maxVisible` tags with no
  // sign that any are missing.
  const effectiveMax = showAll
    ? tags.length
    : (visibleCount ?? (singleLine ? Math.min(1, tags.length) : maxVisible));
  const visibleTags = tags.slice(0, effectiveMax);
  const hiddenTags = tags.slice(effectiveMax);
  const overflow = hiddenTags.length;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex items-center gap-1",
        // One line never wraps, even for the frame before the count is
        // measured: the pills shrink and truncate instead. It fills its cell,
        // so the width measured is the room the cell gives and not the tags'
        // own (already fitted) width, and anything the count still gets wrong
        // (the badge's room is an estimate) is clipped at the cell's edge
        // rather than spilling into the next column.
        singleLine ? "min-w-0 flex-1 flex-nowrap overflow-x-clip" : "flex-wrap",
        className
      )}
    >
      {/* Hidden measurement container — renders all tags for width calculation.
          Only widths are read, so it is clipped to no height: unclipped, every
          tag wraps into a tall invisible column that still counts towards the
          scroll height of the list around it, giving a short list a scrollbar
          and blank space under its last row. */}
      {!showAll && (
        <div
          ref={measureRef}
          aria-hidden="true"
          data-testid="document-tags-measure"
          className="pointer-events-none absolute left-0 top-0 flex h-0 flex-wrap items-center gap-1 overflow-hidden"
          style={{ visibility: "hidden" }}
        >
          {tags.map((tag) => {
            const displayName =
              tag.type === "category" ? formatCategoryDisplay(tag.name) : tag.name;
            return (
              <span
                key={`measure-${tag.type}-${tag.id}`}
                className={cn(
                  DOCUMENT_TAG_PILL_CLASS,
                  documentTagToneClass(TAG_TONES[tag.type]),
                  // Measured as drawn: a short tag still takes the drawn pill's floor.
                  singleLine && PILL_FLOOR_CLASS
                )}
              >
                <span className="truncate">{displayName}</span>
              </span>
            );
          })}
          {/* One line keeps the badge's real room per digit length, so a short
              "+2 more" doesn't cost a tag that would fit next to it. */}
          {singleLine &&
            badgeMeasureCounts(tags.length - 1).map((hidden) => (
              <Badge
                key={`measure-badge-${hidden}`}
                data-badge-measure={hidden}
                variant="outline"
                className={OVERFLOW_BADGE_CLASS}
              >
                +{hidden} more
              </Badge>
            ))}
        </div>
      )}

      {/* Visible tags */}
      {visibleTags.map((tag) => {
        const displayName = tag.type === "category" ? formatCategoryDisplay(tag.name) : tag.name;
        const canRemove = onTagRemove && REMOVABLE_TAG_TYPES.has(tag.type);
        return (
          <DocumentTagPill
            key={`${tag.type}-${tag.id}`}
            label={displayName}
            tone={TAG_TONES[tag.type]}
            onClick={onTagClick ? () => onTagClick(tag) : undefined}
            onRemove={canRemove ? () => onTagRemove(tag) : undefined}
            // On one line a pill is what gives way: it truncates to make room
            // for the badge and the editor, which never shrink, down to a
            // floor that still shows a letter and an ellipsis.
            className={singleLine ? PILL_FLOOR_CLASS : undefined}
          />
        );
      })}
      {overflow > 0 && !showAll && (
        <>
          {onToggleShowAll ? (
            <Badge
              variant="outline"
              className={cn("cursor-pointer hover:bg-muted", singleLine && OVERFLOW_BADGE_CLASS)}
              onClick={onToggleShowAll}
            >
              +{overflow} more
            </Badge>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={cn(
                      onOverflowClick ? "cursor-pointer hover:bg-muted" : "cursor-default",
                      singleLine && OVERFLOW_BADGE_CLASS
                    )}
                    onClick={
                      onOverflowClick
                        ? (e) => {
                            e.stopPropagation();
                            onOverflowClick();
                          }
                        : undefined
                    }
                  >
                    +{overflow} more
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    {hiddenTags
                      .map((tag) =>
                        tag.type === "category" ? formatCategoryDisplay(tag.name) : tag.name
                      )
                      .join(", ")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      )}
      {/* Only when the collapsed row hid something: expanding keeps its fit. */}
      {showAll &&
        onToggleShowAll &&
        tags.length > (visibleCount ?? (singleLineRequested ? 1 : maxVisible)) && (
          <Badge
            variant="outline"
            className="cursor-pointer hover:bg-muted"
            onClick={onToggleShowAll}
          >
            Show less
          </Badge>
        )}
      {suffix &&
        (singleLine ? (
          // Measured, so the tags leave it room on their one line.
          <span ref={setSuffixEl} className="inline-flex flex-shrink-0">
            {suffix}
          </span>
        ) : (
          suffix
        ))}
    </div>
  );
}
